/**
 * MIAOI — Universal Area-of-Interest type used by every spatial analysis.
 *
 * Closes IGIST/MGIS evaluation gaps #7 and #8: every analysis must accept any
 * AOI shape (point, line, polygon, multipolygon-with-holes, circle, bbox,
 * dynamic, global) and honor irregular polygon clipping.
 */

import * as turf from '@turf/turf';

export type MIAOIPoint = {
  kind: 'point';
  position: [number, number];
  /** Optional buffer radius in meters that promotes the AOI to a circle. */
  radius?: number;
};

export type MIAOILine = {
  kind: 'line';
  coordinates: [number, number][];
  /** Optional buffer in meters, producing a polygon AOI from the line. */
  buffer?: number;
};

export type MIAOIPolygonShape = {
  kind: 'polygon';
  ring: [number, number][];
  holes?: [number, number][][];
};

export type MIAOIMultiPolygon = {
  kind: 'multipolygon';
  polygons: { ring: [number, number][]; holes?: [number, number][][] }[];
};

export type MIAOICircle = {
  kind: 'circle';
  center: [number, number];
  radiusMeters: number;
};

export type MIAOIBBox = {
  kind: 'bbox';
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
};

export type MIAOIDynamic = {
  kind: 'dynamic';
  track: () => MIAOI;
};

export type MIAOIGlobal = { kind: 'global' };

export type MIAOI =
  | MIAOIPoint
  | MIAOILine
  | MIAOIPolygonShape
  | MIAOIMultiPolygon
  | MIAOICircle
  | MIAOIBBox
  | MIAOIDynamic
  | MIAOIGlobal;

/** Resolve a possibly-dynamic AOI to a concrete (non-dynamic) AOI. */
export function resolveAOI(aoi: MIAOI): Exclude<MIAOI, MIAOIDynamic> {
  return aoi.kind === 'dynamic' ? (resolveAOI(aoi.track()) as Exclude<MIAOI, MIAOIDynamic>) : aoi;
}

/** Convert an AOI to a GeoJSON Feature (Polygon/MultiPolygon/Point/LineString). */
export function aoiToGeoJSON(aoi: MIAOI): GeoJSON.Feature {
  const a = resolveAOI(aoi);
  switch (a.kind) {
    case 'point': {
      if (a.radius && a.radius > 0) {
        return turf.circle(a.position, a.radius / 1000, { units: 'kilometers' }) as GeoJSON.Feature;
      }
      return turf.point(a.position) as GeoJSON.Feature;
    }
    case 'line': {
      const line = turf.lineString(a.coordinates);
      if (a.buffer && a.buffer > 0) {
        const buffered = turf.buffer(line, a.buffer / 1000, { units: 'kilometers' });
        if (buffered) return buffered as GeoJSON.Feature;
      }
      return line as GeoJSON.Feature;
    }
    case 'polygon': {
      const rings: number[][][] = [a.ring as number[][]];
      if (a.holes) for (const h of a.holes) rings.push(h as number[][]);
      return turf.polygon(rings) as GeoJSON.Feature;
    }
    case 'multipolygon': {
      const polys: number[][][][] = a.polygons.map((p) => {
        const rings: number[][][] = [p.ring as number[][]];
        if (p.holes) for (const h of p.holes) rings.push(h as number[][]);
        return rings;
      });
      return turf.multiPolygon(polys) as GeoJSON.Feature;
    }
    case 'circle':
      return turf.circle(a.center, a.radiusMeters / 1000, { units: 'kilometers' }) as GeoJSON.Feature;
    case 'bbox':
      return turf.bboxPolygon([a.minLon, a.minLat, a.maxLon, a.maxLat]) as GeoJSON.Feature;
    case 'global':
      return turf.bboxPolygon([-180, -85, 180, 85]) as GeoJSON.Feature;
  }
}

/** Compute a [minLon, minLat, maxLon, maxLat] bbox for any AOI. */
export function aoiBBox(aoi: MIAOI): [number, number, number, number] {
  const f = aoiToGeoJSON(aoi);
  const [minX, minY, maxX, maxY] = turf.bbox(f);
  return [minX, minY, maxX, maxY];
}

/** Approximate area in square meters for area-bearing AOIs. */
export function aoiAreaM2(aoi: MIAOI): number {
  const a = resolveAOI(aoi);
  if (a.kind === 'point' && !a.radius) return 0;
  if (a.kind === 'line' && !a.buffer) return 0;
  return turf.area(aoiToGeoJSON(aoi) as any);
}

/** Test whether a [lon,lat] coordinate falls inside the AOI. */
export function aoiContains(aoi: MIAOI, lonLat: [number, number]): boolean {
  const a = resolveAOI(aoi);
  if (a.kind === 'global') return true;
  if (a.kind === 'point') {
    if (!a.radius) return a.position[0] === lonLat[0] && a.position[1] === lonLat[1];
    return turf.distance(turf.point(a.position), turf.point(lonLat), { units: 'meters' }) <= a.radius;
  }
  if (a.kind === 'circle') {
    return turf.distance(turf.point(a.center), turf.point(lonLat), { units: 'meters' }) <= a.radiusMeters;
  }
  const poly = aoiToGeoJSON(aoi);
  if (poly.geometry.type === 'Polygon' || poly.geometry.type === 'MultiPolygon') {
    return turf.booleanPointInPolygon(turf.point(lonLat), poly as any);
  }
  return false;
}
